import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'
import * as ActivityFeedPostsActions from './activity-feed-posts.actions'
import { ActivityFeedPostsEntity } from './activity-feed-posts.models'

export const ACTIVITY_FEED_POSTS_FEATURE_KEY = 'activityFeedPosts'

export interface State extends EntityState<ActivityFeedPostsEntity> {
  selectedId?: string | number // which ActivityFeedPosts record has been selected
  loaded: boolean // has the ActivityFeedPosts list been loaded
  activePage: number // the index of the page of 10 items currently in the view
  next: string | null // the URL for the next set of activity feed posts to be loaded
  count: number // number of total posts
  newActivityFeedPost: string | null // uuid of the latest added activity feed post
  submitting: boolean | null
}

export interface ActivityFeedPostsPartialState {
  readonly [ACTIVITY_FEED_POSTS_FEATURE_KEY]: State
}

function sortByDate(post1: ActivityFeedPostsEntity, post2: ActivityFeedPostsEntity): number {
  return post1.created < post2.created ? 1 : -1
}

export const activityFeedPostsAdapter: EntityAdapter<ActivityFeedPostsEntity> =
  createEntityAdapter<ActivityFeedPostsEntity>({
    selectId: (activityFeedPost: ActivityFeedPostsEntity) => activityFeedPost.uuid,
    sortComparer: sortByDate,
  })

export const initialState: State = activityFeedPostsAdapter.getInitialState({
  // set initial required properties
  next: null,
  activePage: 0,
  loaded: false,
  count: 0,
  newActivityFeedPost: null,
  submitting: false,
})

const activityFeedPostsReducer = createReducer(
  initialState,
  on(ActivityFeedPostsActions.loadActivityFeedPosts, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(ActivityFeedPostsActions.loadActivityFeedPostsSuccess, (state, action) =>
    activityFeedPostsAdapter.setAll(action.activityFeedPosts, {
      ...state,
      next: action.next,
      loaded: true,
    }),
  ),
  on(ActivityFeedPostsActions.loadNextActivityFeedPostsSuccess, (state, action) =>
    activityFeedPostsAdapter.addMany(action.activityFeedPosts, {
      ...state,
      loaded: true,
    }),
  ),
  on(ActivityFeedPostsActions.createActivityFeedPost, (state) => ({
    ...state,
    submitting: true,
  })),
  on(ActivityFeedPostsActions.createActivityFeedPostSuccess, (state) => ({
    ...state,
    submitting: false,
  })),
  on(ActivityFeedPostsActions.createActivityFeedPostFailure, (state) => ({
    ...state,
    submitting: false,
  })),
  on(ActivityFeedPostsActions.loadNextActivityFeedRepliesSuccess, (state, action) =>
    activityFeedPostsAdapter.updateOne(action.update, state),
  ),
  on(ActivityFeedPostsActions.addOneActivityFeedPost, (state, action) =>
    activityFeedPostsAdapter.addOne(action.data, state),
  ),
  on(ActivityFeedPostsActions.addOneActivityFeedPost, (state, action) => ({
    ...state,
    newActivityFeedPost: action.data.uuid,
  })),
  on(ActivityFeedPostsActions.updateActivityFeedPosts, (state, action) =>
    activityFeedPostsAdapter.updateMany(action.activityFeedPosts, state),
  ),
  on(ActivityFeedPostsActions.loadActivityFeedPostsFailure, (state, { error }) => ({ ...state, error })),
  on(ActivityFeedPostsActions.updateActivityFeedPostsNextUrlSuccess, (state, action) => {
    return {
      ...state,
      next: action.next,
    }
  }),
  on(ActivityFeedPostsActions.setActivePage, (state, action) => {
    return {
      ...state,
      activePage: action.pageNumber,
    }
  }),
  on(ActivityFeedPostsActions.updateCount, (state, action) => {
    return {
      ...state,
      count: action.count,
    }
  }),
)

export function reducer(state: State | undefined, action: Action) {
  return activityFeedPostsReducer(state, action)
}
